import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { MunicipalityResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

/**
 * Story 15.1's own "Add a municipality" affordance — a small, standalone
 * dialog calling the identical `POST /municipalities` route the Add
 * Property wizard's own "create a new town" sub-flow already uses
 * (spec judgement call 1).
 *
 * That sub-flow (`AddPropertyWizard.tsx`) is not cleanly extractable on its
 * own: it is woven into the wizard's own step machinery — a
 * `createdMunicipalities` list merged into the picker's own `<Select>`
 * options, a `pendingChecklistMunicipalityIds` set that gates the wizard's
 * "Next" button until the fresh town's setup checklist is resolved, and
 * `MunicipalitySetupChecklist` wired directly into that gating. Pulling
 * that apart would mean extracting all of it into a shared hook the wizard
 * itself would then also have to adopt — a real refactor of an
 * already-shipped, working flow, well beyond this story's own "one new
 * list screen" scope. This dialog instead mirrors `AddTenantDialog.tsx`'s
 * own plain, single-step create-dialog shape: the two fields `POST
 * /municipalities` actually requires (`name`/`county`), a submit that
 * calls the same route, and the caller decides what happens next
 * (`onCreated`). No setup checklist opens here — an Owner who wants to
 * fill one in lands on the new record page next (mirrors
 * `AddPropertyWizard.tsx`'s/`AddTenantDialog.tsx`'s own "create with the
 * essentials, refine on the record page" shape).
 */
export function AddMunicipalityDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [county, setCounty] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Discarded, never persisted anywhere, if the dialog closes before
  // submitting — mirrors `AddTenantDialog.tsx`'s own identical reset-on-close effect.
  useEffect(() => {
    if (!open) {
      setName("");
      setCounty("");
      setError(undefined);
    }
  }, [open]);

  const canSubmit = name.trim() !== "" && county.trim() !== "";

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const created = await apiFetch<MunicipalityResource>("/municipalities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), county: county.trim() }),
      });
      onOpenChange(false);
      onCreated(created.id);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError
          ? submitError.message
          : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a municipality">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("name")}</span>
          <Input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("county")}</span>
          <Input value={county} onChange={(event) => setCounty(event.target.value)} />
        </label>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Adding…" : "Add municipality"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
